import React, { useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from '../redux/hooks';
import { setEditing } from '../redux/slices/profileSlice';
import { useNavigate } from 'react-router-dom';
import { clearAccessToken } from '../redux/slices/authSlice';

const ErrorText = styled.div`
  color: #AE0505;
  font-size: 12px;
  font-family: 'IBM Plex Mono';
  font-weight: 700;
`;

const SuccessText = styled.div`
  color: #2d5748;
  font-size: 12px;
  font-family: 'IBM Plex Mono';
  font-weight: 700;
`;

const MessageContainer = styled.div`
  align-self: start;
  margin-top: -6px;
  gap: 4px;
  display: flex;
  align-items: center;
  justify-content: left;
`;

const ProfileActions: React.FC = () => {
  const dispatch = useDispatch();
  const { isEditing, firstName, lastName, email, phone } = useSelector(state => state.profile);
  const { accessToken, role } = useSelector(state => state.auth);
  const navigate = useNavigate();

  const [message, setMessage] = useState<{ type: 'error' | 'success', text: string } | null>(null);

  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BASE_API_URL}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (err) {
      console.error("Logout request failed", err);
    } finally {
      dispatch(clearAccessToken());
      navigate('/');
    }
  };
  
  const handleSave = async () => {
    const nameRegex = /^[А-ЯІЇЄҐа-яіїєґ'’\- ]+$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+380\d{9}$/;

    if (!nameRegex.test(firstName) || !nameRegex.test(lastName)) {
      setMessage({ type: 'error', text: 'Ім’я та прізвище повинні містити лише українські літери' });
      return;
    }
    if (!emailRegex.test(email)) {
      setMessage({ type: 'error', text: 'Некоректний email' });
      return;
    }
    if (!phoneRegex.test(phone)) {
      setMessage({ type: 'error', text: 'Телефон має бути у форматі +380XXXXXXXXX' });
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}/profile/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          first_name: firstName,
          second_name: lastName,
          email,
          phone,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Помилка при оновленні профілю');
      }

      setMessage({ type: 'success', text: 'Профіль оновлено успішно' });
      dispatch(setEditing(false));
    } catch (err) {
      setMessage({ type: 'error', text: (err as Error).message });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '20px', marginBottom: '30px'}}>
      {message && (
        <MessageContainer>
          {message.type === 'error' ? (
            <ErrorText>{message.text}</ErrorText>
          ) : (
            <SuccessText>{message.text}</SuccessText>
          )}
        </MessageContainer>
      )}
      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>

        <button
          onClick={() => dispatch(setEditing(true))}
          disabled={isEditing}
          style={{
            padding: '10px 20px',
            borderRadius: '10px',
            backgroundColor: '#d8e8e2',
            border: 'none',
            fontSize: '16px',
          }}
        >
          Редагувати
        </button>

        <button
          onClick={handleSave}
          disabled={!isEditing}
          style={{
            padding: '10px 20px',
            borderRadius: '10px',
            backgroundColor: isEditing ? '#2d5748' : '#aabbbb',
            color: 'white',
            border: 'none',
            fontSize: '16px',
          }}
        >
          Зберегти
        </button>

        <button 
          onClick={handleLogout}
          style={{
            padding: '10px 20px',
            borderRadius: '10px',
            backgroundColor: '#ae0404',
            color: 'white',
            border: 'none',
            fontSize: '16px',
          }}
        >
          Вийти
        </button>
      </div>
      { role === 'admin' && 
        <button
          onClick={() => navigate("/admin")}
          disabled={isEditing}
          style={{
            padding: '10px 20px',
            borderRadius: '10px',
            backgroundColor: '#a13b16',
            color: 'white',
            border: 'none',
            fontSize: '16px',
          }}
        >
          Сторінка Адміністратора
        </button>
      }

    </div>
  );
};

export default ProfileActions;
